import type { Visitor } from '@/lib/types/visitor';
import { VisitorType } from '@/lib/types/visitor';
import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

interface TouchedFields {
    title: boolean;
    attendees: boolean;
    visitorFirstName: boolean;
    visitorLastName: boolean;
    visitorEmail: boolean;
    visitorCountry: boolean;
    visitorCommercialSegment: boolean;
    erpCustomer: boolean;
}

interface ValidationResult {
    invalid: boolean;
    message: string;
}

export function useReservationValidation(
    touched: TouchedFields,
    hasVisitors: ComputedRef<boolean> | { value: boolean },
    title: ComputedRef<string> | { value: string },
    description: ComputedRef<string> | { value: string },
    attendees: ComputedRef<number> | { value: number },
    visitor: ComputedRef<Visitor> | { value: Visitor },
    selectedErpCustomer: ComputedRef<any> | { value: any }
) {
    const { t } = useI18n();

    const validation = {
        title: computed((): ValidationResult => ({
            invalid: touched.title && title.value.trim() === '',
            message: t('calendar.new.validation.titleRequired')
        })),

        description: computed((): ValidationResult => ({
            invalid: description.value.trim().length > 255,
            message: t('calendar.new.validation.descriptionTooLong')
        })),

        attendees: computed((): ValidationResult => ({
            invalid: touched.attendees && (!attendees.value || attendees.value <= 0),
            message: t('calendar.new.validation.attendeesRequired')
        })),

        visitorFirstName: computed((): ValidationResult => ({
            invalid: touched.visitorFirstName &&
                hasVisitors.value &&
                visitor.value.type !== VisitorType.ErpProfessional &&
                visitor.value.firstName.trim() === '',
            message: t('calendar.new.validation.firstNameRequired')
        })),

        visitorLastName: computed((): ValidationResult => ({
            invalid: touched.visitorLastName &&
                hasVisitors.value &&
                visitor.value.type !== VisitorType.ErpProfessional &&
                visitor.value.lastName.trim() === '',
            message: t('calendar.new.validation.lastNameRequired')
        })),

        visitorEmail: computed((): ValidationResult => ({
            invalid: touched.visitorEmail &&
                hasVisitors.value &&
                visitor.value.email.trim() === '',
            message: t('calendar.new.validation.emailRequired')
        })),

        visitorCountry: computed((): ValidationResult => ({
            invalid: touched.visitorCountry &&
                hasVisitors.value &&
                visitor.value.type === VisitorType.NewProfessional &&
                !visitor.value.country,
            message: t('calendar.new.validation.countryRequired')
        })),

        visitorCommercialSegment: computed((): ValidationResult => ({
            invalid: touched.visitorCommercialSegment &&
                hasVisitors.value &&
                visitor.value.type === VisitorType.NewProfessional &&
                !visitor.value.commercialSegment,
            message: t('calendar.new.validation.commercialSegmentRequired')
        })),

        erpCustomer: computed((): ValidationResult => ({
            invalid: touched.erpCustomer &&
                hasVisitors.value &&
                visitor.value.type === VisitorType.ErpProfessional &&
                !selectedErpCustomer.value,
            message: t('calendar.new.validation.erpCustomerRequired')
        }))
    };

    const visitorValidationErrors = computed(() => ({
        firstName: validation.visitorFirstName.value.invalid ? validation.visitorFirstName.value.message : undefined,
        lastName: validation.visitorLastName.value.invalid ? validation.visitorLastName.value.message : undefined,
        email: validation.visitorEmail.value.invalid ? validation.visitorEmail.value.message : undefined,
        country: validation.visitorCountry.value.invalid ? validation.visitorCountry.value.message : undefined,
        commercialSegment: validation.visitorCommercialSegment.value.invalid ? validation.visitorCommercialSegment.value.message : undefined,
        erpCustomer: validation.erpCustomer.value.invalid ? validation.erpCustomer.value.message : undefined
    }));

    return {
        validation,
        visitorValidationErrors
    };
}
